<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <!-- Backdrop -->
    <div 
      class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
      @click="$emit('close')"
    ></div>

    <!-- Modal -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative w-full max-w-md transform overflow-hidden rounded-lg bg-gradient-to-r from-content-800 to-content-700 shadow-xl transition-all">
        <!-- Header -->
        <div class="border-b border-content-600 px-6 py-4">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-white">
              Registrar Intención de Inversión
            </h3>
            <button
              @click="$emit('close')"
              class="text-content-200 hover:text-white focus:outline-none button-interactive"
            >
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="px-6 py-4">
          <!-- Opportunity Summary -->
          <div class="mb-6 p-4 bg-content-600 rounded-lg border border-content-500">
            <h4 class="font-medium text-white mb-2">{{ opportunity.title }}</h4>
            <div class="space-y-1 text-sm text-content-200">
              <div v-if="opportunity.enableDebt" class="flex justify-between">
                <span>Inversión mínima (Deuda):</span>
                <span class="font-medium text-white">{{ formatCurrency(opportunity.minimumInvestmentDebt) }}</span>
              </div>
              <div v-if="opportunity.enableCoOwnership" class="flex justify-between">
                <span>Inversión mínima (Adquisición de inmueble):</span>
                <span class="font-medium text-white">{{ formatCurrency(opportunity.minimumInvestmentEquity) }}</span>
              </div>
            </div>
          </div>

          <!-- Form -->
          <form @submit.prevent="submitForm">
            <!-- Investment Type Selector (only if both are enabled) -->
            <div v-if="showInvestmentTypeSelector" class="mb-4">
              <label class="block text-sm font-medium text-white mb-2">
                Tipo de inversión *
              </label>
              <div class="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  @click="form.investmentType = 'debt'"
                  :class="[
                    'p-3 rounded-lg border-2 transition-all text-left',
                    form.investmentType === 'debt'
                      ? 'border-primary-500 bg-primary-500 bg-opacity-20'
                      : 'border-content-500 bg-content-700 hover:border-content-400'
                  ]"
                >
                  <div class="font-medium text-white">Deuda</div>
                  <div class="text-xs text-content-300 mt-1">
                    Tasa Fija: {{ opportunity.annualFixedRate }}%
                  </div>
                  <div class="text-xs text-content-300">
                    Plazo: {{ opportunity.term }}
                  </div>
                </button>
                <button
                  type="button"
                  @click="form.investmentType = 'co-ownership'"
                  :class="[
                    'p-3 rounded-lg border-2 transition-all text-left',
                    form.investmentType === 'co-ownership'
                      ? 'border-primary-500 bg-primary-500 bg-opacity-20'
                      : 'border-content-500 bg-content-700 hover:border-content-400'
                  ]"
                >
                  <div class="font-medium text-white">Adquisición de inmueble</div>
                  <div class="text-xs text-content-300 mt-1">
                    TIR: {{ opportunity.estimatedIRR }}%
                  </div>
                  <div class="text-xs text-content-300">
                    Plazo indefinido
                  </div>
                </button>
              </div>
            </div>

            <!-- Investment Amount -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-white mb-2">
                Monto de inversión *
              </label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 transform -translate-y-1/2 text-content-300">$</span>
                <input
                  v-model.number="form.amount"
                  type="number"
                  :min="currentMinimumInvestment"
                  :step="1000"
                  required
                  class="w-full pl-8 pr-3 py-2 border border-content-500 bg-content-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder-content-300"
                  placeholder="0"
                />
              </div>
              <p v-if="form.amount && form.amount < currentMinimumInvestment" class="mt-1 text-sm text-red-400">
                El monto mínimo es {{ formatCurrency(currentMinimumInvestment) }}
              </p>
            </div>

            <!-- Projected Returns -->
            <div v-if="form.amount && form.amount >= currentMinimumInvestment" class="mb-4 p-4 bg-green-900 bg-opacity-20 border border-green-500 rounded-lg">
              <h5 class="font-medium text-green-400 mb-2">Proyección de retornos</h5>
              <div class="space-y-1 text-sm text-green-300">
                <div class="flex justify-between">
                  <span>Inversión:</span>
                  <span class="font-medium text-white">{{ formatCurrency(form.amount) }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Retorno estimado ({{ currentRate }}% {{ investmentTypeLabel }}):</span>
                  <span class="font-medium text-white">{{ formatCurrency(projectedReturn) }}</span>
                </div>
                <div class="flex justify-between border-t border-green-500 pt-1">
                  <span class="font-medium">Total estimado:</span>
                  <span class="font-bold text-green-400">{{ formatCurrency(totalEstimated) }}</span>
                </div>
              </div>
            </div>


            <!-- Notes -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-white mb-2">
                Comentarios adicionales
              </label>
              <textarea
                v-model="form.notes"
                rows="3"
                class="w-full px-3 py-2 border border-content-500 bg-content-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder-content-300"
                placeholder="Cualquier información adicional que desees compartir..."
              ></textarea>
            </div>

            <!-- Disclaimer -->
            <div class="mb-6 p-4 bg-yellow-900 bg-opacity-20 border border-yellow-500 rounded-lg">
              <p class="text-sm text-yellow-300">
                <strong class="text-yellow-400">Importante:</strong> Esta es una intención de inversión, no un compromiso vinculante. 
                Un representante se pondrá en contacto contigo para completar el proceso.
              </p>
            </div>


            <!-- Form Buttons -->
            <div class="flex justify-end space-x-3 mt-6">
              <button
                @click="$emit('close')"
                type="button"
                class="px-4 py-2 text-sm font-medium text-content-200 bg-content-600 border border-content-500 rounded-lg hover:bg-content-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 smooth button-interactive"
              >
                Cancelar
              </button>
              <button
                @click.prevent="submitForm"
                :disabled="!isFormValid || submitting"
                type="button"
                class="primary-button disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ submitting ? 'Enviando...' : 'Registrar Intención' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../api";
import { is422 } from "../../utils/response";

export default {
  name: 'InvestmentModal',
  props: {
    opportunity: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      submitting: false,
      form: {
        amount: null,
        notes: '',
        investmentType: null // 'debt' or 'co-ownership'
      }
    }
  },
  mounted() {
    // Set default investment type based on what's enabled
    if (this.opportunity.enableDebt && !this.opportunity.enableCoOwnership) {
      this.form.investmentType = 'debt';
    } else if (!this.opportunity.enableDebt && this.opportunity.enableCoOwnership) {
      this.form.investmentType = 'co-ownership';
    } else if (this.opportunity.enableDebt && this.opportunity.enableCoOwnership) {
      // Both enabled, default to debt
      this.form.investmentType = 'debt';
    }
  },
  computed: {
    showInvestmentTypeSelector() {
      return this.opportunity.enableDebt && this.opportunity.enableCoOwnership;
    },
    currentRate() {
      if (this.form.investmentType === 'co-ownership') {
        return this.opportunity.estimatedIRR;
      }
      return this.opportunity.annualFixedRate;
    },
    investmentTypeLabel() {
      return this.form.investmentType === 'co-ownership' ? 'TIR' : 'tasa anual';
    },
    currentMinimumInvestment() {
      if (this.form.investmentType === 'co-ownership') {
        return this.opportunity.minimumInvestmentEquity || 0;
      }
      return this.opportunity.minimumInvestmentDebt || 0;
    },
    isFormValid() {
      return this.form.amount &&
             this.form.amount >= this.currentMinimumInvestment &&
             this.form.investmentType !== null;
    },
    projectedReturn() {
      if (!this.form.amount || !this.currentRate) return 0;
      const termMonths = this.extractMonthsFromTerm(this.opportunity.term);
      const annualReturn = this.form.amount * (this.currentRate / 100);
      return (annualReturn * termMonths) / 12;
    },
    totalEstimated() {
      return this.form.amount + this.projectedReturn;
    }
  },
  methods: {
    
    async submitForm() {
      if (!this.isFormValid || this.submitting) {
        return;
      }

      this.submitting = true;

      try {
        const investmentData = {
          amount: this.form.amount,
          notes: this.form.notes,
          investmentType: this.form.investmentType,
          opportunityId: this.opportunity.id
        };

        const response = await api.opportunities.registerIntention(this.opportunity.id, investmentData);
        this.$store.dispatch("successSnack", response.message || "Intención de inversión registrada exitosamente");
        this.$emit('confirm');
        this.$emit('close');
      } catch (error) {
        if (is422(error)) {
          console.log('Validation errors:', error.response.data.errors);
        }
        
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al registrar la inversión");
      } finally {
        this.submitting = false;
      }
    },
    extractMonthsFromTerm(term) {
      const match = term.match(/(\d+)/);
      return match ? parseInt(match[1]) : 12;
    },
    formatCurrency(amount) {
      const numericAmount = parseFloat(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numericAmount);
    }
  }
}
</script>